import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  fetchTutorialStates,
  resetTutorialStates,
  saveTutorialState,
} from '@/services/tutorialStateService';
import { useAuthStore } from '@/stores/auth';
import { getTour, tours } from '@/tours';
import type { TourDefinition, TourStep, TutorialStatus } from '@/tours/types';
import { logger } from '@/utils/logger';

const PROGRESS_KEY = 'gaia:tour-progress';

interface TourProgress {
  tourKey: string;
  stepIndex: number;
}

/**
 * Owns which tour is running, step navigation, and the per-user
 * completed/dismissed state. The visual side (spotlight, popover, target
 * resolution) lives in TourOverlay.vue; this store is the state machine.
 *
 * Terminal state (completed/dismissed) persists account-wide via
 * tutorialStateService; in-flight progress persists locally so an interrupted
 * tour resumes at its last step instead of restarting.
 */
export const useTourStore = defineStore('tour', () => {
  /** Terminal state per tour key. Absence of a key = tour never finished. */
  // Values are `| undefined`: tours are keyed by arbitrary string keys and a
  // tour the user never touched has no entry.
  const statuses = ref<Record<string, TutorialStatus | undefined>>({});
  /** True once terminal states loaded successfully — auto-start waits for it. */
  const isLoaded = ref(false);
  const activeTourKey = ref<string | null>(null);
  const stepIndex = ref(0);
  /** Direction of the last navigation, so unresolvable steps skip the same way. */
  const lastDirection = ref<1 | -1>(1);
  /** Steps actually shown this run — decides complete-vs-abort at the end. */
  const shownSteps = ref(0);
  /**
   * Set by the overlay around its own router.push calls, so the route watcher
   * can tell tour navigation apart from the user wandering off mid-tour.
   */
  const isTourNavigation = ref(false);
  /** Tours already offered this session — never re-nag within a session. */
  const invitedThisSession = new Set<string>();
  /** First-visit invitation awaiting a yes/no (tour key). */
  const pendingInviteKey = ref<string | null>(null);

  const activeTour = computed<TourDefinition | null>(() =>
    activeTourKey.value ? (getTour(activeTourKey.value) ?? null) : null
  );
  const pendingInvite = computed<TourDefinition | null>(() =>
    pendingInviteKey.value ? (getTour(pendingInviteKey.value) ?? null) : null
  );
  const activeStep = computed<TourStep | null>(
    () => activeTour.value?.steps[stepIndex.value] ?? null
  );
  /** Tours the current user is allowed to run (drives the help menu). */
  const availableTours = computed<TourDefinition[]>(() => tours.filter(hasPermissionFor));

  function hasPermissionFor(tour: TourDefinition): boolean {
    if (!tour.requiredPermission) {
      return true;
    }
    const permissions = useAuthStore().userPermissions;
    const required = Array.isArray(tour.requiredPermission)
      ? tour.requiredPermission
      : [tour.requiredPermission];
    return required.every(flag => permissions[flag]);
  }

  /**
   * Boot check: load the user's terminal states. Failures are swallowed but
   * keep `isLoaded` false, so tours never auto-start on unknown state (a
   * veteran user must not be re-toured because one request failed).
   */
  async function initialize(): Promise<void> {
    try {
      const states = await fetchTutorialStates();
      statuses.value = Object.fromEntries(states.map(s => [s.tutorialKey, s.status]));
      isLoaded.value = true;
      resumeInterruptedTour();
    } catch {
      // Silent — tutorials are never worth breaking bootstrap for.
    }
  }

  /**
   * Saved progress can only exist when the page was reloaded mid-tour (every
   * other way of ending a tour clears it). Starting a tour already marked it
   * as seen, so a reload means the tour is simply over — clean up whatever
   * the interrupted demo left behind (its onExit never got to run) and move
   * on without showing anything.
   */
  function resumeInterruptedTour(): void {
    const saved = readProgress();
    if (!saved || activeTourKey.value) {
      return;
    }
    clearProgress();
    void Promise.resolve(getTour(saved.tourKey)?.onExit?.()).catch(() => {
      // Cleanup is best-effort on boot.
    });
  }

  /** Index the current run resumed at — drives the stale-resume fallback. */
  let runStartIndex = 0;
  let restartedFromStaleResume = false;

  function startTour(key: string, options?: { resume?: boolean }): void {
    const tour = getTour(key);
    if (!tour || !hasPermissionFor(tour)) {
      return;
    }
    pendingInviteKey.value = null;
    // Starting counts as seen: whatever happens next (refresh, abandon,
    // crash), this tour is never offered again. Completing upgrades the
    // status; the help menu can always relaunch regardless.
    if (!statuses.value[key]) {
      statuses.value[key] = 'dismissed';
      void saveTutorialState(key, 'dismissed').catch(() => {
        // Local state is already set.
      });
    }
    const saved = readProgress();
    const resumeIndex =
      options?.resume && saved?.tourKey === key && saved.stepIndex < tour.steps.length
        ? saved.stepIndex
        : 0;
    runStartIndex = resumeIndex;
    restartedFromStaleResume = false;
    stepIndex.value = resumeIndex;
    lastDirection.value = 1;
    shownSteps.value = 0;
    activeTourKey.value = key;
  }

  function next(): void {
    move(1);
  }

  function back(): void {
    move(-1);
  }

  function move(direction: 1 | -1): void {
    if (!activeTour.value) {
      return;
    }
    lastDirection.value = direction;
    const target = stepIndex.value + direction;
    if (target < 0) {
      return;
    }
    if (target >= activeTour.value.steps.length) {
      void complete();
      return;
    }
    stepIndex.value = target;
    writeProgress();
  }

  /**
   * Called by the overlay when a step can't be shown (missing target or
   * failed prerequisite): keep moving in the last direction; running off the
   * end completes the tour if anything was shown, otherwise aborts silently
   * so it stays eligible to run once its targets exist.
   */
  function skipUnresolvableStep(): void {
    if (!activeTour.value) {
      return;
    }
    const target = stepIndex.value + lastDirection.value;
    if (target < 0 || target >= activeTour.value.steps.length) {
      if (shownSteps.value > 0) {
        logger.warn(`[tour] "${activeTourKey.value}" ran out of resolvable steps — completing`);
        void complete();
      } else if (runStartIndex > 0 && !restartedFromStaleResume && stepIndex.value !== 0) {
        // A resumed run where NOTHING could be shown: the saved progress
        // points at state that no longer exists (e.g. the wizard draft the
        // scene depended on is gone). Restart from the top instead of
        // navigating somewhere and silently disappearing.
        logger.warn(
          `[tour] "${activeTourKey.value}" resumed at scene ${runStartIndex} but its state is gone — restarting from the beginning`
        );
        restartedFromStaleResume = true;
        clearProgress();
        lastDirection.value = 1;
        stepIndex.value = 0;
      } else {
        logger.warn(
          `[tour] "${activeTourKey.value}" had no resolvable steps — exiting without recording`
        );
        abort();
      }
      return;
    }
    stepIndex.value = target;
  }

  /** Called by the overlay once a step has actually rendered. */
  function markStepShown(): void {
    shownSteps.value += 1;
    writeProgress();
  }

  async function complete(): Promise<void> {
    await finish('completed');
  }

  /** User skipped/closed the tour — never auto-shown again. */
  async function dismiss(): Promise<void> {
    await finish('dismissed');
  }

  async function finish(status: TutorialStatus): Promise<void> {
    const key = activeTourKey.value;
    end();
    if (!key) {
      return;
    }
    statuses.value[key] = status;
    clearProgress();
    // The end hooks navigate and clean up on the tour's behalf, but the tour
    // is already inactive here — hold the navigation flag so app guards
    // (e.g. a wizard's "leave?" prompt over disposable demo state) stand
    // down for them too.
    isTourNavigation.value = true;
    try {
      if (status === 'completed') {
        await getTour(key)?.onComplete?.();
      } else {
        await getTour(key)?.onExit?.();
      }
    } catch (error) {
      logger.warn(`[tour] end hook for "${key}" failed`, error);
    } finally {
      isTourNavigation.value = false;
    }
    try {
      await saveTutorialState(key, status);
    } catch {
      // Local state is already set — worst case the tour re-offers elsewhere.
    }
  }

  /**
   * End the run (e.g. user navigated away mid-tour, or no step could
   * resolve). The seen-marker was already written at start; progress is
   * cleared so a relaunch always starts deterministically from scene one.
   */
  function abort(): void {
    const key = activeTourKey.value;
    clearProgress();
    end();
    if (key) {
      void Promise.resolve(getTour(key)?.onExit?.()).catch(error => {
        logger.warn(`[tour] onExit for "${key}" failed`, error);
      });
    }
  }

  function end(): void {
    activeTourKey.value = null;
    stepIndex.value = 0;
    shownSteps.value = 0;
    isTourNavigation.value = false;
  }

  /**
   * First-visit hook, called by the overlay on route changes. Tours never
   * start themselves: the first visit to a tour's entry route OFFERS it
   * (once per session) and the user must accept. Leaving the page silently
   * withdraws an unanswered offer; declining records a dismissal so it is
   * never offered again.
   */
  function maybeAutoStart(routeName: string | undefined): void {
    // An unanswered invitation doesn't follow the user around the app.
    if (pendingInvite.value && pendingInvite.value.entryRoute !== routeName) {
      pendingInviteKey.value = null;
    }
    if (!isLoaded.value || activeTourKey.value || typeof routeName !== 'string') {
      return;
    }
    // Eligibility is part of the search, not a check on the winner: more than
    // one tour can share an entry route (home hosts both the welcome tour and
    // the chat), and picking the first match unconditionally would let an
    // already-seen tour permanently shadow the ones behind it. Registration
    // order in `tours` is therefore the offer order.
    const tour = tours.find(
      candidate =>
        candidate.entryRoute === routeName &&
        !statuses.value[candidate.key] &&
        !invitedThisSession.has(candidate.key) &&
        hasPermissionFor(candidate)
    );
    if (!tour) {
      return;
    }
    invitedThisSession.add(tour.key);
    pendingInviteKey.value = tour.key;
  }

  /** The user said yes to a first-visit invitation. */
  function acceptInvite(): void {
    const key = pendingInviteKey.value;
    pendingInviteKey.value = null;
    if (key) {
      startTour(key, { resume: true });
    }
  }

  /** "Not now": never offer this tour again (help menu still relaunches it). */
  function declineInvite(): void {
    const key = pendingInviteKey.value;
    pendingInviteKey.value = null;
    if (!key) {
      return;
    }
    statuses.value[key] = 'dismissed';
    void saveTutorialState(key, 'dismissed').catch(() => {
      // Local state is already set.
    });
  }

  /** Hide an invitation without recording anything (asks again next session). */
  function clearInvite(): void {
    pendingInviteKey.value = null;
  }

  /** Reset from user settings: every tour becomes eligible again. */
  async function resetAll(): Promise<void> {
    await resetTutorialStates();
    statuses.value = {};
    invitedThisSession.clear();
    pendingInviteKey.value = null;
    clearProgress();
  }

  function readProgress(): TourProgress | null {
    try {
      const raw = localStorage.getItem(PROGRESS_KEY);
      return raw ? (JSON.parse(raw) as TourProgress) : null;
    } catch {
      return null;
    }
  }

  function writeProgress(): void {
    if (!activeTourKey.value) {
      return;
    }
    try {
      localStorage.setItem(
        PROGRESS_KEY,
        JSON.stringify({ tourKey: activeTourKey.value, stepIndex: stepIndex.value })
      );
    } catch {
      // Storage unavailable — resume just won't work.
    }
  }

  function clearProgress(): void {
    try {
      localStorage.removeItem(PROGRESS_KEY);
    } catch {
      // Storage unavailable — nothing to clear.
    }
  }

  return {
    statuses,
    isLoaded,
    activeTourKey,
    stepIndex,
    lastDirection,
    isTourNavigation,
    activeTour,
    activeStep,
    availableTours,
    pendingInvite,
    initialize,
    startTour,
    acceptInvite,
    declineInvite,
    clearInvite,
    next,
    back,
    skipUnresolvableStep,
    markStepShown,
    complete,
    dismiss,
    abort,
    maybeAutoStart,
    resetAll,
  };
});
